import { Helmet } from 'react-helmet-async';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Marquee } from '@sorare/core/src/atoms/container/Marquee';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyS,
  BrandM,
  BrandS,
  LabelL,
  LabelM,
} from '@sorare/core/src/atoms/typography';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { NewSeasonLogo } from '@sorare/core/src/components/Season2026/NewSeasonLogo';
import { Section } from '@sorare/core/src/components/landing/Section';
import { ORIGIN } from '@sorare/core/src/config';
import { useEventsContext } from '@sorare/core/src/contexts/events';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { borderGradientStyle } from '@sorare/core/src/style/utils';

import badgeAvif from './assets/badge.avif';
import badge from './assets/badge.webp';
import baseballTeamAvif from './assets/baseball-team.avif';
import baseballTeam from './assets/baseball-team.webp';
import BecomeCreatorAvif from './assets/become-creator.avif';
import BecomeCreator from './assets/become-creator.webp';
import bg from './assets/bg.svg';
import creatorsAvif from './assets/creators.avif';
import creators from './assets/creators.webp';
import fiagoAvif from './assets/creators/fiago.avif';
import fiago from './assets/creators/fiago.webp';
import hunterHimselfAvif from './assets/creators/hunter-himself.avif';
import hunterHimself from './assets/creators/hunter-himself.webp';
import johnNellisAvif from './assets/creators/john-nellis.avif';
import johnNellis from './assets/creators/john-nellis.webp';
import mikeZakarianAvif from './assets/creators/mike-zakarian.avif';
import mikeZakarian from './assets/creators/mike-zakarian.webp';
import piedsCarresAvif from './assets/creators/pieds-carres.avif';
import piedsCarres from './assets/creators/pieds-carres.webp';
import pitchAddictAvif from './assets/creators/pitch-addict.avif';
import pitchAddict from './assets/creators/pitch-addict.webp';
import postUnitedAvif from './assets/creators/post-united.avif';
import postUnited from './assets/creators/post-united.webp';
import opticalStarballAvif from './assets/optical-starball.avif';
import opticalStarball from './assets/optical-starball.webp';
import sorareCreatorsProgramAvif from './assets/sorare-creators-program.avif';
import sorareCreatorsProgram from './assets/sorare-creators-program.webp';

const CreatorProgramRoot = styled(Vertical)`
  padding-bottom: var(--double-unit);
`;

const CreatorProgramTitleImg = styled(Img)`
  max-width: 360px;
  @media ${tabletAndAbove} {
    width: 100%;
    max-width: 600px;
  }
`;

const TileWrapper = styled(Vertical)`
  padding: var(--unit);
  @media ${tabletAndAbove} {
    flex-direction: row;
    flex-wrap: wrap;
  }
`;

const TileContainer = styled(Horizontal)`
  background-image: radial-gradient(
      circle,
      transparent 70%,
      var(--c-nd-100) 100%
    ),
    url(${bg});
  background-position: center;
  background-size: cover;
  border-radius: var(--unit);
  width: 360px;
  height: auto;
  padding: var(--double-unit);
  @media ${tabletAndAbove} {
    height: calc(var(--unit) * 20);
    width: 376px;
  }
`;

const TileName = styled.p`
  color: var(--c-white);
  text-transform: uppercase;
  font-family: var(--brand-font);
  font-size: calc(var(--unit) * 10);
`;

const TileText = styled(Vertical)`
  padding-top: var(--double-unit);
`;

const ProgramDetailsRoot = styled(Vertical)`
  width: var(--100vw);
  height: auto;
  padding: calc(var(--unit) * 5) 0;
  background-image: linear-gradient(
      to bottom,
      var(--c-black) 0%,
      transparent 40%
    ),
    linear-gradient(to left, transparent 50%, var(--c-black) 90%), url(${bg});
  background-color: var(--c-nl-900-opaque);
  background-size: auto;
  background-position: center;
  background-repeat: repeat;
`;

const ProgramDetailsWrapper = styled(Horizontal)`
  flex-wrap: wrap;
  gap: var(--double-unit);
`;

const ProgramDetailsContainer = styled(Vertical)`
  --container-width: 340px;
  position: relative;
  width: var(--container-width);
  min-height: 272px;
  background-color: var(--c-black);
  --border-gradient: linear-gradient(
    to bottom right,
    #ff0000 0%,
    #ffffff 30%,
    #ffffff 50%,
    #106ef2 100%
  );
  --border-size: 2px;
  ${borderGradientStyle};
  pointer-events: none;
  border-radius: calc(var(--unit) * 10);
  padding: var(--half-unit) var(--unit);
  @media ${tabletAndAbove} {
    --container-width: 360px;
    min-height: 300px;
  }
`;

const OpticalStarballImgWrapper = styled.div`
  display: grid;
  grid-template-columns: calc(var(--unit) * 10) 1fr;
  margin-left: var(--half-unit);
`;

const OpticalStarballImg = styled(Img)`
  width: 48px;
  @media ${tabletAndAbove} {
    width: 64px;
  }
`;

const MarqueeCreatorContainer = styled(ButtonBase)`
  position: relative;
  padding-top: var(--triple-unit);
`;

const MarqueeCreatorImage = styled(Img)`
  width: 252px;
`;

const BadgeImg = styled(Img)`
  position: absolute;
  left: calc(-1 * var(--intermediate-unit));
  top: var(--unit);
  width: calc(var(--unit) * 5);
`;

const BaseballTeamImg = styled(Img)`
  width: 952px;
  height: auto;
  background-image: radial-gradient(
      circle,
      transparent 70%,
      var(--c-black) 90%,
      var(--c-black) 100%
    ),
    url(${bg});
  object-fit: contain;
  mask-image: linear-gradient(to bottom, var(--c-black) 50%, transparent 95%);
`;

const BecomeCreatorImg = styled(Img)`
  width: 672px;
`;

const CreatorHeroWrapper = styled(Vertical)`
  width: 100%;
  max-width: calc(var(--container-width) * 2 + var(--double-unit));
  background-image: linear-gradient(
      to bottom right,
      transparent 0%,
      var(--c-nd-50-opaque) 60%,
      var(--c-black) 70%
    ),
    url(${bg});
  background-size: auto;
  background-position: center;
  background-repeat: repeat;
  mask-image: linear-gradient(to top, transparent, white 10%);
  @media ${tabletAndAbove} {
    mask-image: linear-gradient(to top, transparent, white 50%);
  }
`;

const HeroSubtitle = styled(LabelL)`
  max-width: 496px;
  text-align: center;
  line-height: 1.8;
`;

const CreatorsImgWrapper = styled(Horizontal).attrs({ center: true })`
  width: 100%;
  object-fit: cover;
  @media ${tabletAndAbove} {
    margin-top: calc(var(--unit) * -9);
  }
`;

const BecomeACreatorImgWrapper = styled(Vertical)`
  margin-top: calc(var(--unit) * -10);
`;

const HeroWrapper = styled(Vertical).attrs({ gap: 1 })`
  --badge-width: 80px;
  padding-top: var(--unit);
`;

const BadgeWrapper = styled.div`
  width: var(--badge-width);
  align-self: flex-end;
  margin-left: auto;
  @media ${tabletAndAbove} {
    --badge-width: 112px;
    width: var(--badge-width);
  }
`;

const ProgramDetailsTitle = styled(BrandM)`
  word-break: break-word;
  flex-grow: 1;
`;

const ProgramDetailsDescription = styled(LabelM)`
  padding: var(--unit) var(--unit);
`;

const TitleContainer = styled(Horizontal).attrs({ center: true })`
  width: var(--100vw);
`;

const LogoWrapper = styled(Horizontal).attrs({ center: true })`
  flex: 1;
  @media ${tabletAndAbove} {
    margin-left: var(--badge-width);
  }
`;

const messages = defineMessages({
  heroSubtitle: {
    defaultMessage:
      'Earn money by promoting Sorare, where you can buy, trade, and play with official digital cards of football, NBA and MLB players.',
  },
  CTAButton: {
    defaultMessage: 'Become a creator',
  },
  howToTitle: {
    defaultMessage: 'Recommend Sorare. Earn commissions.',
  },
  howToChip: {
    defaultMessage: 'How it works?',
  },
  CreatorsListTitle: {
    defaultMessage: 'Discover some of our Creators',
  },
  CreatorsListChip: {
    defaultMessage: 'Who?',
  },
  programDetailsChip: {
    defaultMessage: 'Why join?',
  },
  programDetailsTitle: {
    defaultMessage: 'Unlock exclusive benefits',
  },
});

const steps = [
  {
    name: '1',
    title: <FormattedMessage defaultMessage="Become a creator" />,
    subtitle: (
      <FormattedMessage defaultMessage="Join over 2,000 creators, publishers, affiliates who are earning wth the program. It's easy and free to join." />
    ),
  },
  {
    name: '2',
    title: <FormattedMessage defaultMessage="Promote Sorare" />,
    subtitle: (
      <FormattedMessage defaultMessage="Create and share your content (videos, articles, ads, etc.) with your Sorare affiliate link." />
    ),
  },
  {
    name: '3',
    title: <FormattedMessage defaultMessage="Earn commission" />,
    subtitle: (
      <FormattedMessage defaultMessage="Earn up to 10% in commissions from new users who purchase cards. Our competitive conversion rates help maximize earnings." />
    ),
  },
];

const programDetails = [
  {
    title: <FormattedMessage defaultMessage="Unique perks for content" />,
    description: (
      <FormattedMessage defaultMessage="Benefit from signed and unsigned jerseys, VIP tickets, event access, and special content creation experiences!" />
    ),
  },
  {
    title: <FormattedMessage defaultMessage="Generous commissions" />,
    description: (
      <FormattedMessage defaultMessage="We set competitive commission rates for all purchases you bring. Earn 10% of all revenue you generate for new customers you bring on." />
    ),
  },
  {
    title: <FormattedMessage defaultMessage="Grow your audience" />,
    description: (
      <FormattedMessage defaultMessage="We take it all and convert into revenue for you. We offer global campaigns, so the world is your customer base - experience strong follower growth!" />
    ),
  },
  {
    title: <FormattedMessage defaultMessage="Dedicated support" />,
    description: (
      <FormattedMessage defaultMessage="Get individual support with any inquiries you might have. Set-up, best practices, you name it – we're here to help." />
    ),
  },
];

const creatorsList = [
  {
    name: 'John Nellis',
    sport: 'Football',
    picture: johnNellis,
    avif: johnNellisAvif,
    link: 'https://www.youtube.com/@JohnNellis',
  },
  {
    name: 'Fiago',
    sport: 'Football',
    picture: fiago,
    avif: fiagoAvif,
    link: 'https://www.youtube.com/c/Fiago',
  },
  {
    name: 'Pieds Carres',
    sport: 'Football',
    picture: piedsCarres,
    avif: piedsCarresAvif,
    link: 'https://www.youtube.com/channel/UCKHZg0TZIXlLUnd1e8oavSQ',
  },
  {
    name: 'Post United',
    sport: 'Football',
    picture: postUnited,
    avif: postUnitedAvif,
    link: 'https://www.youtube.com/channel/UCrhu_g7xvgsetoN0yNOr3fQ/videos',
  },
  {
    name: 'Pitch Addict',
    sport: 'Football',
    picture: pitchAddict,
    avif: pitchAddictAvif,
    link: 'https://www.youtube.com/@pitchaddict',
  },
  {
    name: 'Hunter Himself',
    sport: 'MLB',
    picture: hunterHimself,
    avif: hunterHimselfAvif,
    link: 'https://www.youtube.com/@Hunter_Himself',
  },
  {
    name: 'Mike Zakarian',
    sport: 'NBA',
    picture: mikeZakarian,
    avif: mikeZakarianAvif,
    link: 'https://www.youtube.com/@NBABIT',
  },
];

const HowToToTile = () => {
  return (
    <TileWrapper gap={3} center>
      {steps.map(step => (
        <TileContainer gap={2} key={step.name}>
          <TileName color="var(--c-white)">{step.name}</TileName>

          <TileText gap={2}>
            <BrandM color="var(--c-white)">{step.title}</BrandM>
            <LabelM color="var(--c-white)">{step.subtitle}</LabelM>
          </TileText>
        </TileContainer>
      ))}
    </TileWrapper>
  );
};

const ProgramDetails = () => {
  return (
    <ProgramDetailsRoot center gap={2}>
      <ProgramDetailsWrapper center>
        {programDetails.map(detail => (
          <ProgramDetailsContainer center gap={1} key={detail.title.toString()}>
            <OpticalStarballImgWrapper>
              <picture>
                <source srcSet={opticalStarballAvif} type="image/avif" />
                <OpticalStarballImg
                  src={opticalStarball}
                  fetchPriority="low"
                  alt=""
                />
              </picture>
              <ProgramDetailsTitle
                style={{ flexGrow: 1 }}
                color="var(--c-white)"
              >
                {detail.title}
              </ProgramDetailsTitle>
            </OpticalStarballImgWrapper>
            <ProgramDetailsDescription>
              {detail.description}
            </ProgramDetailsDescription>
          </ProgramDetailsContainer>
        ))}
      </ProgramDetailsWrapper>
    </ProgramDetailsRoot>
  );
};

const CreatorsList = () => {
  return (
    <Marquee gap={2} duration="25s">
      {creatorsList.map(creator => (
        <MarqueeCreatorContainer
          key={creator.name}
          to={creator.link}
          as={Link}
          target="_blank"
          rel="noopener noreferrer"
        >
          <BadgeImg
            src={badge}
            fetchPriority="low"
            alt={creator.name}
            width="324"
            height="564"
          />
          <picture>
            <source srcSet={creator.avif} type="image/avif" />
            <MarqueeCreatorImage
              src={creator.picture}
              fetchPriority="low"
              alt={creator.name}
              width="324"
              height="564"
            />
          </picture>
          <BrandS>{creator.name}</BrandS>
          <BodyS>{creator.sport}</BodyS>
        </MarqueeCreatorContainer>
      ))}
    </Marquee>
  );
};

const Hero = ({ trackBecomeACreator }: { trackBecomeACreator: () => void }) => {
  const { formatMessage } = useIntl();
  const isLaptopAndAbove = useIsLaptop();
  const description = formatMessage({
    defaultMessage: "An image displaying the title 'Sorare Creators Program'",
  });

  return (
    <HeroWrapper center gap={0}>
      <TitleContainer>
        <LogoWrapper>
          <NewSeasonLogo size={isLaptopAndAbove ? 'large' : 'medium'} />
        </LogoWrapper>
        <BadgeWrapper>
          <picture>
            <source srcSet={badgeAvif} type="image/avif" />
            <Img src={badge} fetchPriority="low" alt="" />
          </picture>
        </BadgeWrapper>
      </TitleContainer>
      <picture>
        <source srcSet={sorareCreatorsProgramAvif} type="image/avif" />
        <CreatorProgramTitleImg
          src={sorareCreatorsProgram}
          fetchPriority="low"
          alt={formatMessage({
            defaultMessage: 'Sorare Creators Program',
          })}
        />
      </picture>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Sorare Creators Program',
            url: 'https://sorare.com/creator-program',
            image: {
              '@type': 'ImageObject',
              url: `${ORIGIN}${sorareCreatorsProgram}`,
              name: 'Sorare Creators Program',
              description,
            },
          })}
        </script>
      </Helmet>
      <Vertical gap={2} center>
        <HeroSubtitle color="var(--c-white)">
          <FormattedMessage {...messages.heroSubtitle} />
        </HeroSubtitle>
        <Button
          externalLink
          onClick={trackBecomeACreator}
          href="https://app.impact.com/campaign-campaign-info-v2/Sorare.brand?io=hCbouJYwr0NS9SOoLhETNm%2FEUqHP0wTCKfEq7pPbc5BE8T7OrNKRvU94SpUPlNTR"
          size="large"
          color="secondary"
        >
          <FormattedMessage {...messages.CTAButton} />
        </Button>
      </Vertical>
      <CreatorsImgWrapper>
        <picture>
          <source srcSet={creatorsAvif} type="image/avif" />
          <Img src={creators} fetchPriority="low" alt="" width="1456" />
        </picture>
      </CreatorsImgWrapper>
    </HeroWrapper>
  );
};

export const CreatorProgram = () => {
  const { track } = useEventsContext();

  const trackBecomeACreator = () => {
    track('Become a Sorare Creator');
  };
  return (
    <CreatorProgramRoot center gap={8}>
      <CreatorHeroWrapper center gap={2}>
        <Hero trackBecomeACreator={trackBecomeACreator} />
      </CreatorHeroWrapper>
      <Section
        gap={2}
        chipTitle={<FormattedMessage {...messages.howToChip} />}
        title={<FormattedMessage {...messages.howToTitle} />}
        titleMaxWidth="496px"
      >
        <HowToToTile />
      </Section>
      <Section
        gap={2}
        chipTitle={<FormattedMessage {...messages.programDetailsChip} />}
        title={<FormattedMessage {...messages.programDetailsTitle} />}
      >
        <ProgramDetails />
      </Section>
      <Section
        gap={2}
        title={<FormattedMessage {...messages.CreatorsListTitle} />}
        chipTitle={<FormattedMessage {...messages.CreatorsListChip} />}
      >
        <CreatorsList />
      </Section>
      <Vertical gap={2} center>
        <picture>
          <source srcSet={baseballTeamAvif} type="image/avif" />
          <BaseballTeamImg
            src={baseballTeam}
            fetchPriority="low"
            alt=""
            width="952"
            height="672"
          />
        </picture>
        <BecomeACreatorImgWrapper>
          <picture>
            <source srcSet={BecomeCreatorAvif} type="image/avif" />
            <BecomeCreatorImg
              src={BecomeCreator}
              fetchPriority="low"
              alt=""
              width="776"
              height="216"
            />
          </picture>
        </BecomeACreatorImgWrapper>
      </Vertical>
      <Button
        externalLink
        onClick={trackBecomeACreator}
        href="https://app.impact.com/campaign-campaign-info-v2/Sorare.brand?io=hCbouJYwr0NS9SOoLhETNm%2FEUqHP0wTCKfEq7pPbc5BE8T7OrNKRvU94SpUPlNTR"
        size="large"
        color="secondary"
      >
        <FormattedMessage {...messages.CTAButton} />
      </Button>
    </CreatorProgramRoot>
  );
};
